import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Reveal from '../../components/Reveal';
// 🚨 This said the sample loans were "originated in Texas and Florida" to investors, while Doorly holds no
// Florida licence, and 13 of its 25 rows were in Florida. Rebuilt Texas-only (Hannah, 2026-11-03). The rows and
// every summary figure come from constants/sampleTape.js, and the licence status from the licence mirror.
import { licenseStatusSentence } from '../../constants/marketExpansion';
import { SAMPLE_LOAN_TAPE, samplePoolSummary, samplePoolDate, samplePoolStatus } from '../../constants/sampleTape';

/**
 * DoorlyMarket — Sample Loan Tape (public).
 *
 * Linked from the "View Sample Tape" CTA on /market. Renders an
 * institutional-style loan tape viewer with portfolio summary cards
 * and an illustrative Texas-only dataset (constants/sampleTape.js). Brand styling matches
 * MarketLanding exactly (#3a6ad9 / #fffd5b accents, white cards,
 * blue footer).
 */

const POOL_SUMMARY = samplePoolSummary();
const LOAN_TAPE = SAMPLE_LOAN_TAPE;

const COLUMNS = [
  { label: 'Loan ID',          field: 'loan_id',           sticky: true,  align: 'left'  },
  { label: 'Property Address', field: 'address',                          align: 'left'  },
  { label: 'Prop Type',        field: 'property_type',                    align: 'left'  },
  { label: 'Note Date',        field: 'note_date',                        align: 'left'  },
  { label: 'Status',           field: 'status',            kind: 'status' },
  { label: 'Amort',            field: 'amort_type',                       align: 'left'  },
  { label: 'Purchase Price',   field: 'purchase_price',                   align: 'right' },
  { label: 'Down %',           field: 'down_pct',                         align: 'right' },
  { label: 'Loan Amount',      field: 'loan_amount',                      align: 'right' },
  { label: 'Prin. Balance',    field: 'principal_balance',                align: 'right' },
  { label: 'LTV',              field: 'ltv',                              align: 'right' },
  { label: 'FICO',             field: 'fico',                             align: 'right' },
  { label: 'Doc Type',         field: 'doc_type',          kind: 'doc'    },
  { label: 'DTI',              field: 'dti',                              align: 'right' },
  { label: 'Rate',             field: 'rate',                             align: 'right' },
  { label: 'Term',             field: 'term',                             align: 'right' },
  { label: 'P&I Payment',      field: 'pi',                               align: 'right' },
  { label: 'First Payment',    field: 'first_payment',                    align: 'left'  },
  { label: 'MTG Lates',        field: 'lates',                            align: 'left'  },
  { label: 'Maturity',         field: 'maturity',                         align: 'left'  },
  { label: 'OO',               field: 'owner_occupied',    align: 'center' },
];

export default function SampleTape() {
  const navigate = useNavigate();
  useEffect(() => { document.title = 'Doorly | Sample Loan Tape'; }, []);

  const REGISTER_PATH = '/market/register';

  const cellBase = {
    padding: '10px 14px',
    fontSize: '13px',
    color: '#111827',
    borderBottom: '1px solid #e5e7eb',
    whiteSpace: 'nowrap',
  };
  const headerCell = {
    padding: '10px 14px',
    fontSize: '12px',
    fontWeight: 700,
    color: '#ffffff',
    backgroundColor: '#111111',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #111111',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">

      {/* ── NAV ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between" style={{ height: '4.5rem' }}>
            <Link to="/market" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <img
                src="/doorly-market-logo.png"
                alt="Doorly Market"
                style={{ height: '40px', width: 'auto', display: 'block' }}
              />
            </Link>
            <Link to="/market" className="text-sm font-semibold text-black hover:text-doorly-blue transition-colors duration-200" style={{ textDecoration: 'none' }}>
              ← Back to DoorlyMarket
            </Link>
          </div>
        </div>
      </nav>

      {/* ── PAGE TITLE ── */}
      <section className="pt-28 sm:pt-36 pb-12 px-4 sm:px-6 lg:px-8" style={{ background: '#3a6ad9' }}>
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wider"
              style={{
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: '#ffffff',
              }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#ffffff' }} />
              Sample Tape
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight tracking-tight mb-3 text-white">
              Doorly <span style={{ color: '#fffd5b' }}>AFM<sup style={{ fontSize: '0.5em', verticalAlign: 'super' }}>™</sup></span> — Sample Loan Tape
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <div className="text-sm sm:text-base font-semibold mb-4" style={{ color: 'rgba(255,255,255,0.85)' }}>
              Pool Date: {samplePoolDate()} &nbsp;|&nbsp; {LOAN_TAPE.length} Loans &nbsp;|&nbsp; {samplePoolStatus()}
            </div>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-sm sm:text-base leading-relaxed max-w-4xl" style={{ color: 'rgba(255,255,255,0.7)' }}>
              This tape represents a sample of Doorly-originated Ability-First Mortgage<sup style={{ fontSize: '0.65em', verticalAlign: 'super' }}>™</sup> loans. Loan IDs, addresses, and borrower data are illustrative. All loans are owner-occupied, first-lien, 30-year fixed-rate residential mortgages. {licenseStatusSentence()}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── PORTFOLIO SUMMARY CARDS ── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="inline-block text-xs font-bold uppercase mb-5"
              style={{ color: '#3a6ad9', letterSpacing: '0.1em', padding: '4px 14px', background: '#edf1fb', borderRadius: '20px', border: '1px solid #c5d3f5' }}>
              Portfolio Summary
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {POOL_SUMMARY.map((m, i) => {
              const isAccent = i % 2 === 0;
              return (
                <Reveal key={m.label} delay={i * 50} y={10}>
                  <div className="rounded-lg p-4 sm:p-5 h-full" style={{
                    background: isAccent ? '#eff4ff' : '#ffffff',
                    border: `1px solid ${isAccent ? '#bfcff8' : '#e5e7eb'}`,
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                      {m.label}
                    </div>
                    <div style={{
                      fontSize: m.value.length > 18 ? '14px' : '20px',
                      fontWeight: 800,
                      color: isAccent ? '#3a6ad9' : '#111111',
                      lineHeight: 1.2,
                    }}>
                      {m.value}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── LOAN TAPE TABLE ── */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="inline-block text-xs font-bold uppercase mb-4"
              style={{ color: '#3a6ad9', letterSpacing: '0.1em', padding: '4px 14px', background: '#edf1fb', borderRadius: '20px', border: '1px solid #c5d3f5' }}>
              Loan-Level Detail
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-black mb-2 leading-tight" style={{ letterSpacing: '-0.5px' }}>
              {LOAN_TAPE.length}-Loan Sample Tape
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mb-6">
              Scroll horizontally to view all columns. Loan ID stays pinned to the left.
            </p>
          </Reveal>

          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}>
            <div className="scroll-x" style={{ maxHeight: '70vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', minWidth: 1400, borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
                <thead>
                  <tr>
                    {COLUMNS.map(col => (
                      <th key={col.field}
                        style={{
                          ...headerCell,
                          textAlign: col.align || 'left',
                          ...(col.sticky ? { left: 0, zIndex: 3, backgroundColor: '#111111' } : {}),
                        }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {LOAN_TAPE.map((loan, rowIdx) => {
                    const rowBg = rowIdx % 2 === 0 ? '#ffffff' : '#f9fafb';
                    return (
                      <tr key={loan.loan_id}>
                        {COLUMNS.map(col => {
                          const v = loan[col.field];
                          const cellStyle = {
                            ...cellBase,
                            textAlign: col.align || 'left',
                            backgroundColor: rowBg,
                            ...(col.sticky ? { position: 'sticky', left: 0, zIndex: 1, fontWeight: 700, color: '#111827' } : {}),
                          };

                          if (col.kind === 'status') {
                            return (
                              <td key={col.field} style={cellStyle}>
                                <span style={{
                                  display: 'inline-block',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '3px 10px',
                                  borderRadius: '9999px',
                                  background: '#f0fde4',
                                  color: '#111111',
                                  border: '1px solid #d4f9a0',
                                }}>
                                  {v}
                                </span>
                              </td>
                            );
                          }
                          if (col.kind === 'doc') {
                            const isFull = v === 'Full Doc';
                            return (
                              <td key={col.field} style={cellStyle}>
                                <span style={{
                                  display: 'inline-block',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '3px 10px',
                                  borderRadius: '9999px',
                                  background: isFull ? '#eff4ff' : '#f3f4f6',
                                  color: isFull ? '#3a6ad9' : '#374151',
                                  border: `1px solid ${isFull ? '#bfcff8' : '#e5e7eb'}`,
                                }}>
                                  {v}
                                </span>
                              </td>
                            );
                          }
                          return <td key={col.field} style={cellStyle}>{v}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="scroll-hint" style={{ padding: '6px 12px' }}>← Scroll horizontally to see all columns →</div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="px-5 sm:px-10 py-6 sm:py-7 mt-auto" style={{
        background: '#3a6ad9',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
            Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 sm:gap-x-6">
            {[
              { label: 'Sign In',                  path: '/market/login' },
              { label: 'Request Access',           path: REGISTER_PATH },
              { label: 'Investor Dashboard',       path: '/market/dashboard' },
              { label: 'Support',                  path: 'mailto:investors@godoorly.com' },
            ].map(link => (
              <span
                key={link.label}
                onClick={() => link.path.startsWith('mailto') ? (window.location.href = link.path) : navigate(link.path)}
                style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'color 0.15s' }}
                onMouseEnter={e => e.target.style.color = '#fffd5b'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.5)'}
              >
                {link.label}
              </span>
            ))}
          </div>
        </div>

        <div style={{
          fontSize: '11px',
          color: 'rgba(255,255,255,0.35)',
          lineHeight: 1.6,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          paddingTop: '14px',
          marginTop: '4px',
        }}>
          DoorlyMarket is intended solely for institutional and accredited counterparties. Access to loan-level information and offerings is subject to approval and confidentiality requirements. Loan IDs and addresses shown are illustrative sample data only.
        </div>
      </footer>
    </div>
  );
}
